/**
 * AliceBlue WebSocket Service for Real-time Market Data
 * Uses the working API Key method
 */

import secureStorage from '../utils/secureStorage';

class AliceBlueWebSocket {
  constructor() {
    this.ws = null;
    this.isConnected = false;
    this.isConnecting = false;
    this.sessionData = null;
    this.subscribers = new Map(); // callback functions for price updates
    this.heartbeatInterval = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectTimer = null;
    this.pendingSubscriptions = []; // Queue for subscriptions during connection
    this.pendingDepthSubscriptions = []; // Queue for depth subscriptions
  }

  /**
   * Initialize and connect to AliceBlue WebSocket
   */
  async connect() {
    // Prevent multiple simultaneous connection attempts
    if (this.isConnecting) {
      console.log('⏳ Connection already in progress, waiting...');
      return this.waitForConnection();
    }

    // If already connected, return immediately
    if (this.isConnected && this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.log('✅ Already connected');
      return Promise.resolve();
    }

    this.isConnecting = true;

    try {
      console.log('🔌 Initializing AliceBlue WebSocket...');

      // Get userSession from secure storage (encrypted sessionStorage)
      const authData = secureStorage.getItem('authData');
      let userSession = authData?.userSession;
      let userId = authData?.userId;
      
      // If not in secure storage, try to get from database via API
      if (!userSession && userId) {
        console.log('⚠️ UserSession not in storage, fetching from database...');
        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
        
        try {
          const response = await fetch(`${API_BASE_URL}/api/auth/session/${userId}`);
          if (response.ok) {
            const data = await response.json();
            if (data.success && data.data.userSession) {
              userSession = data.data.userSession;
              console.log('✅ Retrieved userSession from database');
            }
          }
        } catch (err) {
          console.error('Failed to fetch session from database:', err);
        }
      }
      
      if (!userSession) {
        console.error('❌ No userSession found in secure storage or database');
        console.log('AuthData available:', !!authData);
        throw new Error('No user session found. Please login first.');
      }
      
      console.log('✅ Found userSession:', userSession.substring(0, 50) + '...');

      // Step 1: Create WebSocket session using OAuth userSession
      console.log('📡 Requesting WebSocket session from backend...');
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
      const response = await fetch(`${API_BASE_URL}/api/auth/ws-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userSession })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ HTTP Error:', response.status, response.statusText, errorText);
        throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
      }

      const data = await response.json();
      console.log('📡 Session response:', data);
      
      if (!data.success) {
        console.error('❌ WebSocket session creation failed:', data.error);
        throw new Error('Failed to create WebSocket session: ' + (data.error || 'Unknown error'));
      }

      if (!data.data) {
        console.error('❌ No session data in response:', data);
        throw new Error('Failed to create WebSocket session: No session data in response');
      }

      if (!data.data.wsSess) {
        console.error('❌ No wsSess in session data:', data.data);
        throw new Error('Failed to create WebSocket session: No wsSess in session data');
      }

      this.sessionData = data.data;
      
      // If userId is not in session data, try to get it from localStorage
      if (!this.sessionData.userId) {
        const storedUserId = localStorage.getItem('userId') || sessionStorage.getItem('userId');
        if (storedUserId) {
          this.sessionData.userId = storedUserId;
          console.log('✅ Retrieved userId from storage:', storedUserId);
        } else {
          console.warn('⚠️ No userId found in session data or storage');
        }
      }
      
      console.log('✅ WebSocket session created');
      console.log('📋 Session data:', {
        userId: this.sessionData.userId,
        wsEndpoint: this.sessionData.wsEndpoint,
        hasWsSess: !!this.sessionData.wsSess,
        hasSessionId: !!this.sessionData.sessionId,
        hasAuthToken: !!this.sessionData.authToken,
        hasOrderToken: !!this.sessionData.orderToken
      });

      // Step 2: Generate double SHA-256 authentication token
      // Use sessionId if available, otherwise fallback to wsSess
      const tokenToHash = this.sessionData.sessionId || this.sessionData.wsSess;
      const authToken = await this.generateAuthToken(tokenToHash);
      console.log('🔐 Auth token generated:', authToken.substring(0, 20) + '...');
      console.log('🔑 Token source:', this.sessionData.sessionId ? 'sessionId' : 'wsSess');

      // Step 3: Check if backend is properly configured
      console.log('🔍 Checking backend configuration...');
      const configResponse = await fetch(`${API_BASE_URL}/api/auth/config-check`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      if (configResponse.ok) {
        const configData = await configResponse.json();
        console.log('⚙️ Backend config:', configData);
      } else {
        console.warn('⚠️ Could not check backend configuration');
      }

      // Step 4: Connect to AliceBlue WebSocket with timeout
      const wsUrl = this.sessionData.wsEndpoint || 'wss://ws2.aliceblueonline.com/NorenWS/';
      console.log('🌐 Connecting to:', wsUrl);
      
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          console.error('❌ WebSocket connection timeout after 30 seconds');
          console.error('WebSocket state:', this.ws?.readyState);
          this.isConnecting = false;
          
          if (this.ws) {
            try { 
              this.ws.close();
            } catch (closeErr) {
              console.error('Error closing timed out WebSocket:', closeErr);
            }
            this.ws = null;
          }
          
          // Provide a clear error
          const err = new Error(`WebSocket connection timed out after 30 seconds. URL: ${wsUrl}`);
          
          if (this.connectReject) {
            this.connectReject(err);
            this.connectReject = null;
            this.connectResolve = null;
          }
          
          // Attempt reconnect
          this.attemptReconnect();
        }, 30000); // Increased to 30 seconds

        // Store resolve/reject for auth acknowledgment
        this.connectResolve = resolve;
        this.connectReject = reject;
        this.connectTimeout = timeout;

        try {
          this.ws = new WebSocket(wsUrl);
        } catch (wsError) {
          clearTimeout(timeout);
          this.isConnecting = false;
          console.error('❌ Failed to create WebSocket:', wsError);
          reject(new Error(`Failed to create WebSocket: ${wsError.message}`));
          return;
        }
        
        // Add connection state tracking
        const connectionStartTime = Date.now();

        this.ws.onopen = () => {
          const connectionTime = Date.now() - connectionStartTime;
          console.log(`✅ WebSocket connected successfully in ${connectionTime}ms`);
          console.log('WebSocket readyState:', this.ws.readyState);
          
          // Verify connection is truly open
          if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
            console.error('❌ WebSocket reports open but readyState is not OPEN:', this.ws?.readyState);
            const err = new Error(`WebSocket in invalid state: ${this.ws?.readyState}`);
            if (this.connectReject) {
              clearTimeout(timeout);
              this.connectReject(err);
              this.connectReject = null;
              this.connectResolve = null;
            }
            return;
          }
          
          // Wait for connection to be fully established before sending auth
          setTimeout(() => {
            // Double-check the connection is still open
            if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
              console.error('❌ WebSocket closed before auth could be sent');
              console.error('Current readyState:', this.ws?.readyState);
              console.error('Connection was open for:', Date.now() - connectionStartTime, 'ms');
              
              if (this.connectReject) {
                clearTimeout(timeout);
                this.isConnecting = false;
                this.connectReject(new Error('WebSocket closed before authentication'));
                this.connectReject = null;
                this.connectResolve = null;
              }
              return;
            }

            // Step 4: Authenticate - always use double SHA-256 hash
            const authMessage = {
              susertoken: authToken,
              t: 'c',
              actid: `${this.sessionData.userId}_API`,
              uid: `${this.sessionData.userId}_API`,
              source: 'API'
            };
            
            console.log('📤 Sending authentication message:', {
              actid: authMessage.actid,
              uid: authMessage.uid,
              source: authMessage.source,
              susertoken: authMessage.susertoken.substring(0, 20) + '...'
            });
            
            try {
              this.ws.send(JSON.stringify(authMessage));
              console.log('✅ Authentication message sent');
            } catch (sendError) {
              console.error('❌ Failed to send auth message:', sendError);
              if (this.connectReject) {
                this.connectReject(sendError);
                this.connectReject = null;
                this.connectResolve = null;
              }
            }
          }, 300); // Increased delay for more stable connection
        };

        this.ws.onmessage = (event) => {
          // console.log('📨 Received message:', event.data);
          this.handleMessage(JSON.parse(event.data));
        };

        this.ws.onerror = (error) => {
          clearTimeout(timeout);
          this.isConnecting = false;
          console.error('❌ WebSocket error:', error);
          console.error('Error details:', {
            type: error.type,
            target: error.target,
            readyState: this.ws?.readyState
          });
          if (this.connectReject) {
            this.connectReject(error);
            this.connectReject = null;
            this.connectResolve = null;
          }
        };

        this.ws.onclose = (event) => {
          clearTimeout(timeout);
          this.isConnecting = false;
          console.log('🔌 WebSocket disconnected');
          console.log('Close event:', {
            code: event.code,
            reason: event.reason,
            wasClean: event.wasClean
          });
          this.isConnected = false;
          this.stopHeartbeat();
          // Notify subscribers about disconnection
          this.notifyConnectionStatus(false);
          
          // If we haven't resolved the connection promise yet, reject it
          if (this.connectReject) {
            this.connectReject(new Error(`WebSocket closed: ${event.reason || 'Unknown reason'} (code: ${event.code})`));
            this.connectReject = null;
            this.connectResolve = null;
          }
          
          // Only attempt reconnect if not manually disconnected
          if (event.code !== 1000) {
            this.attemptReconnect();
          }
        };
      });

    } catch (error) {
      this.isConnecting = false;
      console.error('❌ Failed to connect:', error);
      throw error;
    }
  }

  /**
   * Wait for existing connection attempt to complete
   */
  waitForConnection() {
    return new Promise((resolve, reject) => {
      const checkInterval = setInterval(() => {
        if (!this.isConnecting) {
          clearInterval(checkInterval);
          if (this.isConnected) {
            resolve();
          } else {
            reject(new Error('Connection failed'));
          }
        }
      }, 100);

      // Timeout after 15 seconds
      setTimeout(() => {
        clearInterval(checkInterval);
        reject(new Error('Connection wait timeout'));
      }, 15000);
    });
  }

  /**
   * Generate double SHA-256 authentication token
   */
  async generateAuthToken(wsSess) {
    const encoder = new TextEncoder();
    
    const hash1 = await crypto.subtle.digest('SHA-256', encoder.encode(wsSess));
    const hex1 = Array.from(new Uint8Array(hash1))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
    
    const hash2 = await crypto.subtle.digest('SHA-256', encoder.encode(hex1));
    const hex2 = Array.from(new Uint8Array(hash2))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
    
    return hex2;
  }

  /**
   * Handle incoming WebSocket messages
   */
  handleMessage(message) {
    // Log ALL raw messages for debugging depth data
    if (message.t === 'dk' || message.t === 'df') {
      // console.log('📨 RAW DEPTH MESSAGE:', JSON.stringify(message));
    }
    
    // Connection acknowledgment
    if (message.t === 'ck') {
      if (message.s === 'OK') {
        console.log('✅ Authentication successful!');
        this.isConnecting = false;
        
        // Ensure WebSocket is fully OPEN before marking as connected
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          this.isConnected = true;
          this.reconnectAttempts = 0;
          
          // Resolve the connection promise if it's pending
          if (this.connectResolve) {
            clearTimeout(this.connectTimeout);
            this.connectResolve();
            this.connectResolve = null;
            this.connectReject = null;
            this.connectTimeout = null;
          }
          
          this.startHeartbeat();
          
          // Notify subscribers that connection is ready
          this.notifyConnectionStatus(true);
          
          // Process any pending subscriptions
          setTimeout(() => {
            this.processPendingSubscriptions();
          }, 200);
        } else {
          console.warn('⚠️ Auth successful but WebSocket not OPEN, readyState:', this.ws?.readyState);
        }
      } else {
        console.error('❌ Authentication failed');
        this.isConnecting = false;
        
        // Reject the connection promise if it's pending
        if (this.connectReject) {
          clearTimeout(this.connectTimeout);
          this.connectReject(new Error('Authentication failed: ' + (message.s || 'Unknown error')));
          this.connectResolve = null;
          this.connectReject = null;
          this.connectTimeout = null;
        }
      }
    }

    // Subscription acknowledgment
    if (message.t === 'tk') {
      // console.log('✅ Subscribed to:', `${message.e}|${message.tk} - ${message.ts || 'Unknown'}`);
    }

    // Depth acknowledgment (initial depth data when subscribed)
    if (message.t === 'dk') {
      const key = `${message.e}|${message.tk}`;
      
      const depthData = {
        type: 'depth',
        tbq: message.tbq !== undefined ? parseInt(message.tbq) : undefined,  // Total Buy Quantity
        tsq: message.tsq !== undefined ? parseInt(message.tsq) : undefined,  // Total Sell Quantity
        lp: message.lp !== undefined ? parseFloat(message.lp) : undefined,   // Last Price
        exchange: message.e,
        token: message.tk
      };

      // console.log('📊 Depth acknowledgment:', key, 'TBQ:', depthData.tbq, 'TSQ:', depthData.tsq, 'LTP:', depthData.lp);

      // Notify all subscribers
      this.notifySubscribers(key, depthData);
    }

    // Depth feed (includes tbq, tsq)
    if (message.t === 'df') {
      const key = `${message.e}|${message.tk}`;
      
      const depthData = {
        type: 'depth',
        tbq: message.tbq !== undefined ? parseInt(message.tbq) : undefined,  // Total Buy Quantity
        tsq: message.tsq !== undefined ? parseInt(message.tsq) : undefined,  // Total Sell Quantity
        lp: message.lp !== undefined ? parseFloat(message.lp) : undefined,   // Last Price
        exchange: message.e,
        token: message.tk
      };

      // console.log('📊 Depth feed update:', key, 'TBQ:', depthData.tbq, 'TSQ:', depthData.tsq, 'LTP:', depthData.lp);

      // Notify all subscribers
      this.notifySubscribers(key, depthData);
    }

    // Tick feed (Live price update)
    if (message.t === 'tf') {
      const key = `${message.e}|${message.tk}`;
      
      // Parse price data with all available fields
      const lp = parseFloat(message.lp);
      const priceData = {
        ltp: (lp && lp > 0) ? lp : undefined,  // LTP (Last traded price)
        lp: (lp && lp > 0) ? lp : undefined,   // Also keep as lp for backward compatibility
        pc: message.pc !== undefined ? parseFloat(message.pc) : undefined,  // Percentage change
        cv: message.cv !== undefined ? parseFloat(message.cv) : undefined,  // Change value (Absolute change)
        v: message.v !== undefined ? parseInt(message.v) : undefined,       // Volume
        o: message.o !== undefined ? parseFloat(message.o) : undefined,     // Open
        h: message.h !== undefined ? parseFloat(message.h) : undefined,     // High
        l: message.l !== undefined ? parseFloat(message.l) : undefined,     // Low
        c: message.c !== undefined ? parseFloat(message.c) : undefined,     // Close
        ap: message.ap !== undefined ? parseFloat(message.ap) : undefined,  // Average Price
        symbol: message.symbol,                                              // Symbol Name
        exchange: message.e,
        token: message.tk
      };

      // Debug log to see all data including close price
      if (priceData.ltp || priceData.c) {
        // console.log('📊 Price update:', key, 'LTP:', priceData.ltp, 'Close:', priceData.c, 'Open:', priceData.o);
      }

      // Notify all subscribers
      this.notifySubscribers(key, priceData);
    }
  }

  /**
   * Check if WebSocket is truly ready for subscriptions
   */
  isReady() {
    return this.ws && 
           this.ws.readyState === WebSocket.OPEN && 
           this.isConnected;
  }

  /**
   * Subscribe to symbols for live price updates
   */
  subscribe(symbols) {
    // If connecting, queue the subscription
    if (this.isConnecting) {
      console.log('⏳ WebSocket connecting, queuing subscription...');
      this.pendingSubscriptions.push(symbols);
      return;
    }

    // Check WebSocket state first (most reliable)
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn(`⚠️ WebSocket not ready (readyState: ${this.ws?.readyState}). Queueing subscription...`);
      this.pendingSubscriptions.push(symbols);
      // Attempt to connect if not already connecting
      if (!this.isConnecting && !this.isConnected) {
        this.connect().catch(err => console.error('Failed to auto-connect:', err));
      }
      return;
    }

    // Secondary check for isConnected flag
    if (!this.isConnected) {
      console.warn('⚠️ WebSocket connected but not authenticated. Queueing subscription...');
      this.pendingSubscriptions.push(symbols);
      return;
    }

    // Format: "EXCHANGE|TOKEN#EXCHANGE|TOKEN"
    const subscriptionString = symbols
      .map(s => `${s.exchange}|${s.token}`)
      .join('#');

    const subscribeMessage = {
      k: subscriptionString,
      t: 't'
    };

    // console.log('📡 Subscribing to symbols:', symbols);
    // console.log('📡 Subscription string:', subscriptionString);
    try {
      this.ws.send(JSON.stringify(subscribeMessage));
    } catch (error) {
      console.error('Failed to send subscription:', error);
      this.pendingSubscriptions.push(symbols);
    }
  }

  /**
   * Subscribe to depth data (includes tbq, tsq)
   */
  subscribeDepth(symbols) {
    // If connecting, queue the subscription
    if (this.isConnecting) {
      console.log('⏳ WebSocket connecting, queuing depth subscription...');
      this.pendingDepthSubscriptions.push(symbols);
      return;
    }

    // Check WebSocket state first (most reliable)
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn(`⚠️ WebSocket not ready (readyState: ${this.ws?.readyState}). Queueing depth subscription...`);
      this.pendingDepthSubscriptions.push(symbols);
      // Attempt to connect if not already connecting
      if (!this.isConnecting && !this.isConnected) {
        this.connect().catch(err => console.error('Failed to auto-connect:', err));
      }
      return;
    }

    // Secondary check for isConnected flag
    if (!this.isConnected) {
      console.warn('⚠️ WebSocket connected but not authenticated. Queueing depth subscription...');
      this.pendingDepthSubscriptions.push(symbols);
      return;
    }

    const subscriptionString = symbols
      .map(s => `${s.exchange}|${s.token}`)
      .join('#');

    const subscribeMessage = {
      k: subscriptionString,
      t: 'd'  // 'd' for depth data
    };

    // console.log('📊 Subscribing to depth data:', symbols);
    try {
      this.ws.send(JSON.stringify(subscribeMessage));
    } catch (error) {
      console.error('Failed to send depth subscription:', error);
      this.pendingDepthSubscriptions.push(symbols);
    }
  }

  /**
   * Process any pending subscriptions after connection is established
   */
  processPendingSubscriptions() {
    if (this.pendingSubscriptions.length > 0) {
      console.log(`📥 Processing ${this.pendingSubscriptions.length} pending subscriptions...`);
      const pending = [...this.pendingSubscriptions];
      this.pendingSubscriptions = [];
      pending.forEach(symbols => this.subscribe(symbols));
    }

    if (this.pendingDepthSubscriptions.length > 0) {
      console.log(`📥 Processing ${this.pendingDepthSubscriptions.length} pending depth subscriptions...`);
      const pending = [...this.pendingDepthSubscriptions];
      this.pendingDepthSubscriptions = [];
      pending.forEach(symbols => this.subscribeDepth(symbols));
    }
  }

  /**
   * Unsubscribe from symbols
   */
  unsubscribe(symbols) {
    if (!this.isConnected || !this.ws) return;

    const unsubscribeString = symbols
      .map(s => `${s.exchange}|${s.token}`)
      .join('#');

    const unsubscribeMessage = {
      k: unsubscribeString,
      t: 'u'
    };

    this.ws.send(JSON.stringify(unsubscribeMessage));
    // console.log('📡 Unsubscribing from:', unsubscribeString);
  }

  /**
   * Add a subscriber callback for price updates
   */
  addSubscriber(key, callback) {
    if (!this.subscribers.has(key)) {
      this.subscribers.set(key, new Set());
    }
    this.subscribers.get(key).add(callback);
  }

  /**
   * Remove a subscriber callback
   */
  removeSubscriber(key, callback) {
    if (this.subscribers.has(key)) {
      this.subscribers.get(key).delete(callback);
    }
  }

  /**
   * Notify subscribers of price updates
   */
  notifySubscribers(key, data) {
    if (this.subscribers.has(key)) {
      this.subscribers.get(key).forEach(callback => callback(data));
    }
    
    // Also notify wildcard subscribers (*)
    if (this.subscribers.has('*')) {
      this.subscribers.get('*').forEach(callback => callback(data));
    }
  }

  /**
   * Notify connection status
   */
  notifyConnectionStatus(connected) {
    if (this.subscribers.has('_connection')) {
      this.subscribers.get('_connection').forEach(callback => callback(connected));
    }
  }

  /**
   * Start heartbeat (every 50 seconds)
   */
  startHeartbeat() {
    this.heartbeatInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ k: '', t: 'h' }));
        // console.log('💓 Heartbeat sent');
      }
    }, 50000);
  }

  /**
   * Stop heartbeat
   */
  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Attempt to reconnect
   */
  attemptReconnect() {
    // Clear any existing reconnect timer
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('❌ Max reconnection attempts reached. Please refresh the page or check your network connection.');
      this.isConnecting = false;
      // Notify subscribers about permanent disconnection
      this.notifyConnectionStatus(false);
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts - 1), 30000);
    
    console.log(`🔄 Reconnecting in ${delay / 1000}s... (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
    console.log('Last error:', this.lastError || 'Unknown');
    
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      console.log(`🔄 Reconnection attempt ${this.reconnectAttempts} starting...`);
      this.connect().catch(err => {
        console.error('Reconnection failed:', err);
        this.lastError = err.message;
      });
    }, delay);
  }

  /**
   * Disconnect
   */
  disconnect() {
    console.log('🔌 Disconnecting WebSocket...');
    this.stopHeartbeat();
    
    // Clear reconnect timer
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    
    // Clear pending subscriptions
    this.pendingSubscriptions = [];
    this.pendingDepthSubscriptions = [];
    
    if (this.ws) {
      try {
        this.ws.close(1000, 'Manual disconnect'); // 1000 = normal closure
      } catch (err) {
        console.error('Error closing WebSocket:', err);
      }
      this.ws = null;
    }
    
    this.isConnected = false;
    this.isConnecting = false;
    this.sessionData = null;
    this.reconnectAttempts = 0;
    
    // Clean up connection promise handlers
    if (this.connectTimeout) {
      clearTimeout(this.connectTimeout);
      this.connectTimeout = null;
    }
    this.connectResolve = null;
    this.connectReject = null;
  }
}

// Export singleton instance
const aliceBlueWS = new AliceBlueWebSocket();
export default aliceBlueWS;
